import { h, Component } from 'preact';
import Store from 'store/store';
import 'store/bolt';
import 'store/bolt/calendarRemind';
import 'store/bolt/cookies';
import 'store/bolt/ageGate';
import 'store/bolt/pixels';
import 'store/bolt/evidon';
import 'store/bolt/subscriberPopup';
import * as tracking from 'store/bolt/trackingV2';
import pure from 'utils/pure';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import { accessNested, joinClasses } from 'utils/utils';
import tryToPlayVideo from 'utils/tryToPlayVideo';
import tabbingDetection from 'utils/tabbingDetection';
import hoverDetection from 'utils/hoverDetection';
import parseQueryString from 'utils/parseQueryString';
import { getValueForClientSize, addCSSRuleset, getPageIdFromURL, getRelativePageUrl } from 'utils/bolt';
import { useDynamicAssetFolder } from 'utils/assets';
import { getPercentageThreshold } from 'utils/visibilityChecks';
import { shouldShowShowtimes, navigateInternally } from 'utils/bolt';
import { getIsDesktopSize, getIsSinglePageIframed } from 'utils/bolt/client';
import { getCookie } from 'utils/cookies';
import { getHeaderHeight } from 'utils/bolt';
import { getCopy } from 'utils/copy';
import Header from 'components/bolt/header/header';
import Footer from 'components/bolt/footer/footer';
// import SignUpForm from 'components/bolt/signUpForm/signUpForm';
import LandscapeOverlay from 'components/bolt/landscapeOverlay/landscapeOverlay';
// import TrailerOverlay from 'components/bolt/trailerOverlay/trailerOverlay';
import DynamicPageLoader from 'components/shared/dynamicPageLoader/dynamicPageLoader';
import {
	pages,
	SidePanelLoader,
	VideoOverlayLoader
} from 'pages/bolt_pages';
import { pixelsCategories } from 'pages/bolt_features';
import * as webp from 'utils/bolt/webp';
import { isWebPSupported } from 'utils/isWebPSupported';

if (typeof window !== 'undefined') {
	window.navigateInternally = navigateInternally;
}

import { marked } from 'marked';
const markedRenderer = new marked.Renderer();

const replaceCopyVariables = (text) => {
	return text.replace(/(\\)?(\$[0-9A-Z_]+)/g, (match, backslash, name) => {
		if (backslash) return name;
		return getCopy(name) || name;
	});
};

markedRenderer.html = (html) => {
	return replaceCopyVariables(html);
};

markedRenderer.text = (text) => {
	return replaceCopyVariables(text);
};

markedRenderer.link = (href, title, text) => {
	let attributes = {};
	if (title) attributes.title = title;
	if (href && href[0] === '+') {
		href = href.slice(1);
		attributes.target = '_blank';
	}
	try {
		href = encodeURI(href).replace(/%25/g, '%');
	} catch (e) {
		return text;
	}
	if (href && !/^((https?:)|mailto|(#\$))/.test(href)) {
		let pageId = href;
		href = getRelativePageUrl(pageId) || '#';
		attributes.onclick = `event.preventDefault();navigateInternally('${pageId}');`;
		attributes.role = 'button';
	}
	attributes.href = href;
	let attrString = Object.keys(attributes).map(key => key + '="' + attributes[key] + '"').join(' ');
	return '<a ' + attrString + '>' + text + '</a>';
};
marked.setOptions({
	renderer: markedRenderer,
	// breaks: true,
	smartLists: true
});

import s from 'base/bolt/boltApp.sss';

const isServer = typeof window === 'undefined';

export default @pure class BoltApp extends Component {
	constructor(props) {
		super(props);

		this.state.appState = props.initialStore;
		this.state.orientation = props.initialStore?.client?.orientation || 'portrait';
		this.state.bgImageLoaded = false;
		this.state.backgroundStyle = {};
		this.state.fallbackBackgroundStyle = {};
		this.state.posterVisible = true;
		this.state.audioMuted = true;
		// Make isComponentLoaded "not loaded" only if the initial page is actually a page
		this.state.isComponentLoaded = !pages[this.state.appState.activePage];
		Store.set(props.initialStore);

		if (!isServer) {
			tracking.init(props.initialStore.appData?.tracking);
			this.handleResize();
			Store.emit('LANDING', { page: getPageIdFromURL(props.initialStore.appData?.pages) || window.__START_PAGE_ID__ });
		}

		// Store.on('afterAll', (...data) => console.info('STORE EVENT:', ...data));
		Store.on('update', () => {
			const store = Store.get();
			this.setState({ appState: store });
		});

		this.scrolling = this.scrolling.bind(this);
		this.handleResize = this.handleResize.bind(this);
		this.handlePopState = this.handlePopState.bind(this);
		this.handleOrientationChange = this.handleOrientationChange.bind(this);
		this.updateVisibilityPercentages = this.updateVisibilityPercentages.bind(this);
		this.updateBgStyle = this.updateBgStyle.bind(this);
		this.hidePoster = this.hidePoster.bind(this);
		this.removeBgVideo = this.removeBgVideo.bind(this);
		this.playVideo = this.playVideo.bind(this);
		this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
		this.onPageContentLoad = this.onPageContentLoad.bind(this);
		this.toggleAudio = this.toggleAudio.bind(this);

		let appData = props.initialStore?.appData;
		let features = appData?.options;

		const pagesData = appData?.pages || {};
		const pageWithBgData = Object.values(pagesData).find(page => page?.type !== 'showtimes' && page?.data?.bg);
		this.backgroundData = pageWithBgData?.data?.bg;
		this.ageGateEnabled = features?.ageGate;
		this.externalLinkModalEnabled = features?.externalLinkModal === true || features?.externalLinkModal?.enabled;

		this.powsterCookiesEnabled = features?.cookiesModal?.enableModal;
		this.tealiumCookiesEnabled = features?.enableTealiumCookieConsent;
		this.evidonCookiesEnabled = features?.requireUserConsent;
		this.ensightenCookiesEnabled = features?.enableEnsighten;
		this.oneTrustCookiesEnabled = features?.oneTrustCookieConsent;
		this.didomiCookiesEnabled = features?.didomiCookieConsent;
		this.SMECookiesEnabled = features?.SMECookieConsent;
		this.cookiesEnabled = this.powsterCookiesEnabled || this.tealiumCookiesEnabled || this.ensightenCookiesEnabled || this.evidonCookiesEnabled || this.oneTrustCookiesEnabled || this.didomiCookiesEnabled || this.SMECookiesEnabled;;
	}

	componentDidMount() {
		this.isCalendarRemindMode = !shouldShowShowtimes();
		const calendarNeeded = this.calendarNeeded();
		tabbingDetection();
		hoverDetection();
		window.addEventListener('orientationchange', this.handleOrientationChange);
		window.addEventListener('resize', this.handleResize);
		window.addEventListener('scroll', this.scrolling, true);
		window.addEventListener('popstate', this.handlePopState);
		document.addEventListener('visibilitychange', this.handleVisibilityChange);

		this.handleOrientationChange();

		document.body.classList.add('js-loaded');

		Store.get().client.set({ headerHeight: getHeaderHeight() });

		let query = parseQueryString();
		Store.emit('LANDING_QUERY', query);

		if (this.cookiesEnabled) {
			Store.emit('CHECK_COOKIES', pixelsCategories);
			import(/* webpackChunkName: "cookies" */ 'components/bolt/cookies/cookies').then(component => {
				this.Cookies = component.default;
				this.setState({ cookiesCompLoaded: true }); // Trigger a re-render to make sure banner shows
			});

			import(/* webpackChunkName: "cookiesPreference" */ 'components/bolt/cookiesPreference/cookiesPreference').then(component => {
				this.CookiesPreference = component.default;
			});
		} else {
			Store.emit('ACCEPT_ALL_COOKIES');
		}

		if (this.ageGateEnabled) {
			const cookieName = this.state.appState?.appData?.options?.ageGate?.cookieName || 'pow-agegate';
			const lockOut = this.state.appState?.appData?.options?.ageGate?.lockOut;
			const ofAge = query.ag === 'adult' ? true : getCookie(cookieName);
			if (ofAge !== null) {
				Store.emit('AGEGATE_CONSENT', +ofAge);
				// this.ageGateEnabled = false;
				if (!lockOut || ofAge > 0) {
					Store.emit('AGEGATE_CLOSE');
				}
			}
			import(/* webpackChunkName: "ageGate" */ 'components/bolt/ageGate/ageGate').then(component => {
				this.AgeGate = component.default;
				this.setState({ ageGateCompLoaded: true });
			});
		}

		if (this.externalLinkModalEnabled) {
			import(/* webpackChunkName: "externalLinkModal" */ 'components/bolt/externalLinkModal/externalLinkModal').then(component => {
				this.ExternalLinkModal = component.default;
			});
		}

		if (this.state.appState?.appData?.options?.videoOverlay) {
			import(/* webpackChunkName: "trailerOverlay" */ 'components/bolt/trailerOverlay/trailerOverlay').then(component => {
				this.TrailerOverlay = component.default;
			});
		}

		if (Object.values(this.state.appState?.appData?.pages).find(page => page.type === 'pressKit')) {
			import(/* webpackChunkName: "pressKitGalleryOverlay" */ 'components/bolt/pressKit/pressKitGalleryOverlay').then(component => {
				this.PressKitGalleryOverlay = component.default;
			});
		}

		if (this.state.appState?.appData?.options?.landingAudio) {
			import(/* webpackChunkName: "landingAudio" */ 'components/bolt/landingAudio/landingAudio').then(component => {
				this.LandingAudio = component.default;
			});
		}

		const signUpBanner = this.state.appState?.appData?.options?.formEmbeds?.signUp;
		if (Object.values(signUpBanner || {}).filter(e => e).length) {
			import(/* webpackChunkName: "signUpForm" */ 'components/bolt/signUpForm/signUpForm').then(component => {
				this.SignUpForm = component.default;
			});
		}

		if ((this.isCalendarRemindMode || calendarNeeded) && !this.state.calRemindLoaded && !this.calRemindLoadStarted) {
			this.calRemindLoadStarted = true;
			import(/* webpackChunkName: "calendarRemind" */ 'components/bolt/calendarRemind/calendarRemind').then(component => {
				this.CalRemind = component.default;
			});
		}

		this.updateBgStyle();

		this.updateVisibilityPercentages();
		// Refresh things (active section, percentages) when things are loaded
		Store.on('SECTION_LOADED', this.scrolling);

		const visibleScrollbar = accessNested(this.state.appState, 'appData.options.paramountFeatures.visibleScrollbar', {});
		const colorVariables = accessNested(this.state.appState, 'appData.styling.variables', {});
		if (visibleScrollbar?.color) {
			let color = visibleScrollbar.color.replace('$', '');
			addCSSRuleset('#mainScroll::-webkit-scrollbar-thumb', {
				background: colorVariables[color] || color
			});
		}
		if (visibleScrollbar?.backgroundColor) {
			let color = visibleScrollbar.backgroundColor.replace('$', '');
			addCSSRuleset('#mainScroll::-webkit-scrollbar-track', {
				background: colorVariables[visibleScrollbar.color] || color
			});
		}

		const mailchimpData = accessNested(this.state.appState, 'appData.options.formEmbeds.popup', {});
		const { id, baseUrl, uuid, lid, landingDelay } = mailchimpData;
		(!id && (baseUrl && uuid && lid && landingDelay)) && Store.emit('SUBSCRIBER_POPUP', mailchimpData, true);
	}

	componentWillUnmount() {
		window.removeEventListener('orientationchange', this.handleOrientationChange);
		window.removeEventListener('scroll', this.scrolling, true);
		window.removeEventListener('popstate', this.handlePopState);
		document.removeEventListener('visibilitychange', this.handleVisibilityChange);
		Store.off('SECTION_LOADED', this.scrolling);
		clearAllBodyScrollLocks();
		window.clearTimeout(this.videoTimeout);
	}

	componentDidUpdate(prevProps, prevState) {
		// This is being called constantly
		// this.handleOrientationChange();
		if (prevState.appState.activePage !== this.state.appState.activePage) {
			this.updateVisibilityPercentages();
		}

		if (!this.cookieHeight && !isServer) {
			const cookieContainer = document.querySelector('[data-section=cookies]');
			this.cookieHeight = cookieContainer && window.getComputedStyle(cookieContainer).height;
		}

		const isDesktopSize = accessNested(this.state, 'appState.client.isDesktopSize');
		const prevIsDesktopSize = accessNested(prevState, 'appState.client.isDesktopSize');
		const isDesktopWidth = accessNested(this.state, 'appState.client.isDesktopWidth');
		const prevIsDesktopWidth = accessNested(prevState, 'appState.client.isDesktopWidth');

		if (isDesktopSize !== prevIsDesktopSize || isDesktopWidth !== prevIsDesktopWidth) {
			Store.get().client.set({ headerHeight: getHeaderHeight() });
		}

		const { homeActivePercentage } = this.state.appState;
		const homePercentagePausePoint = isDesktopSize ? 0.6 : 0.4;

		if (this.$bgVideo) {
			const loopDisabled = this.getBgValue('disableLoop');
			const videoEnded = this.$bgVideo.currentTime >= this.$bgVideo.duration;
			if (homeActivePercentage >= homePercentagePausePoint && this.$bgVideo.paused && !(loopDisabled && videoEnded)) {
				this.$bgVideo.play();
			} else if (homeActivePercentage < homePercentagePausePoint) {
				this.$bgVideo.pause();
			}
		}

		if (this.state.appState.debugMode && !this.loadingDebugInfo) {
			this.loadingDebugInfo = true;
			import(/* webpackChunkName: "debugInfo" */ 'components/bolt/debugInfo/debugInfo').then(component => {
				this.DebugInfo = component.default;
				this.forceUpdate();
			});
		}

		let bgSources = [...this.$bgVideo?.children || []].map(s => s.src).join(',');
		if (this.previousBgSources && bgSources !== this.previousBgSources) {
			this.$bgVideo?.load();
		}
		this.previousBgSources = bgSources;
	}

	handleVisibilityChange() {
		if (typeof document === 'undefined' || document.hidden || !this.$bgVideo) {
			return;
		}
		let stopped = this.$bgVideo.paused || this.$bgVideo.currentTime === 0 || this.$bgVideo.ended;
		if (stopped && this.$bgVideo.readyState >= 2) {
			// For some reason if you play it too soon on IOS, it freezes even though the video meets all the conditions above
			this.videoTimeout = window.setTimeout(() => {
				// Make sure the video still exists after the timeout
				if (this.$bgVideo) {
					this.$bgVideo.play();
				}
			}, 100);
		}
	}

	updateBgStyle() {
		if (this.backgroundData) {
			if (this.backgroundData.useSolidBgColor) {
				return;
			}

			this.setState({
				backgroundStyle: {
					backgroundImage: this.getBgImage(),
					backgroundPosition: this.getBgValue('bgPosition')
				},
				fallbackBackgroundStyle: { objectPosition: this.getBgValue('bgPosition') }
			});
		}
	}

	removeBgVideo() {
		this.setState({ bgVideo: false });
	}

	scrolling() {
		const headerOpen = accessNested(this.state, 'appState.header.open');
		const ignoreScroll = accessNested(this.state, 'appState.ignoreScroll');

		if (headerOpen || ignoreScroll) {
			return;
		}

		this.updateVisibilityPercentages();
	}

	updateVisibilityPercentages() {
		Store.emit('UPDATE_VISIBILITY_PERCENTAGES');
	}

	calendarNeeded() {
		const noShowtimesButtonPath = accessNested(this.props, 'initialStore.appData.pages.boltShowtimes.data.noShowtimes.buttons.extraButton.path', null);
		const homeButton1Path = accessNested(this.props, 'initialStore.appData.pages.boltHome.data.button1.path', null);
		const homeButton2Path = accessNested(this.props, 'initialStore.appData.pages.boltHome.data.button2.path', null);
		const persistentPath = accessNested(this.props, 'initialStore.appData.header.powsterHeader.persistentButton.path', null);
		return [noShowtimesButtonPath, homeButton1Path, homeButton2Path, persistentPath].includes('@calendarreminder');
	}

	getBgValue(field) {
		if (!this.backgroundData || !this.backgroundData.desktop && !this.backgroundData.tablet && !this.backgroundData.mobile) {
			return undefined;
		}

		let bgValue = {};

		Object.entries(this.backgroundData).map(([device, data]) => {
			bgValue[device] = data[field];
		});

		return useDynamicAssetFolder(getValueForClientSize(bgValue));
	}

	getBgImage() {
		let asset = this.getBgValue('asset');
		const fallbackFormat = accessNested(Store.get(), 'appData.pages.boltHome.data.bg.alternativeImageFormat', 'jpg');
		let videoExtensions = ['mp4', 'webm', 'mov'];

		// If asset is a video, bg image will be the fallback image instead
		if (videoExtensions.includes(asset?.split?.('.').pop())) {
			this.setState({ bgVideo: asset });
			asset = this.getBgValue('fallbackImg');
		}

		// Makes sure the background image is fully loaded before showing it
		if (!this.state.bgImageLoaded) {
			if (asset) {
				if (!this.occluderTimeout) {
					const image = new Image();
					image.onload = () => this.setState({ bgImageLoaded: true });
					image.onerror = image.onload;
					image.src = webp.getAssetUrl(asset, fallbackFormat);
					// If the image takes too long to load, better to show the content
					this.occluderTimeout = setTimeout(() => {
						if (!this.state.bgImageLoaded) {
							this.setState({ bgImageLoaded: true });
						}
					}, 5000);
				}
			} else {
				this.setState({ bgImageLoaded: true });
			}
		}

		return webp.css(asset, fallbackFormat);
	}

	getH1(appData, page, activePageDetails, activePage) {
		let fullPageName = [
			appData?.meta?.title?.locale,
			appData?.copy?.baseCopy[page?.name] || appData?.copy?.baseCopy[activePageDetails?.name] || page?.name || activePageDetails?.name || activePage?.replace(/^bolt/, '')
		].filter(e => e).join(' | ');

		if (activePageDetails?.h1) {
			fullPageName = activePageDetails.h1;
		}

		return fullPageName;
	}

	closeAllOverlays() {
		Store.emit('CLOSE_ALL_OVERLAYS');
	}

	handleOrientationChange() {
		this.handleResize();
		let orientation = 'portrait';
		switch (window.orientation) {
			case 0:
			case 180:
				// Portrait
				orientation = 'portrait';
				break;
			case -90:
			case 90:
				orientation = 'landscape';
				break;
		}
		this.setState({ orientation });
	}

	handleResize() {
		Store.emit('UPDATE_CLIENT');
		let vh = window.innerHeight;
		document.documentElement.style.setProperty('--vh', (vh * 0.01) + 'px');
		document.documentElement.style.setProperty('--windowHeight', vh + 'px');
		let isDesktopSize = getIsDesktopSize();
		let client = this.state.appState.client;
		if (client && isDesktopSize !== client.isDesktopSize) {
			Store.get().client.set({ isDesktopSize });
		}
		this.updateBgStyle();
	}

	hidePoster() {
		this.setState({ posterVisible: false });
	}

	handlePopState(e) {
		let newPage = e.state?.activePage;
		if (!newPage) {
			// Page id not in popped state, try to detect from URL
			newPage = getPageIdFromURL();
		}
		Store.emit('POP_STATE', newPage || window.__START_PAGE_ID__);
	}

	onPageContentLoad() {
		if (this.state.isComponentLoaded !== true) {
			this.setState({ isComponentLoaded: true });
		}
	}

	getExtra() { }

	playVideo(e) {
		tryToPlayVideo(e.target).then(this.hidePoster).catch(this.removeBgVideo);
	}

	toggleAudio() {
		if (!this.$bgVideo) {
			return;
		}

		this.setState({ audioMuted: !this.$bgVideo.muted });
		this.$bgVideo.muted = !this.$bgVideo.muted;
	}

	checkIfLoaded() {
		return this.state.isComponentLoaded && (this.state.bgImageLoaded || !this.backgroundData || this.backgroundData.useSolidBgColor);
	}

	renderBgVideo(homeActivePercentage, backgroundStyle, fallbackBackgroundStyle) {
		const { bgVideo, appState, posterVisible } = this.state;
		const { activePage } = appState;
		const isPageDedicatedPage = (activePage !== 'boltHome') && !appState.appData.pages[activePage]?.section;
		const preLoadImg = this.getBgValue('preLoadImg');
		const fallbackFormat = accessNested(Store.get(), 'appData.pages.boltHome.data.bg.alternativeImageFormat', 'jpg');
		const showtimesShowing = accessNested(appState, 'showtimes.show', false);
		const hideBackground = !homeActivePercentage || showtimesShowing || isPageDedicatedPage;
		const accessibilityCopy = accessNested(appState, 'appData.copy.accessibilityCopy', {});

		return (
			<div class={joinClasses({ s, bgVideo: true, hideBackground })} key="bgVideo">
				<video
					style={backgroundStyle}
					muted={this.state.audioMuted}
					autoPlay
					playsInline
					loop={!this.getBgValue('disableLoop')}
					poster={isWebPSupported ? preLoadImg : preLoadImg.replace(/\.[^.]*$/, fallbackFormat)}
					onCanPlay={this.playVideo}
					onError={this.removeBgVideo}
					ref={$ref => this.$bgVideo = $ref}
					ariaLabel={accessibilityCopy.$BACKGROUND_VIDEO_ARIA}
				>
					{bgVideo.endsWith('.webm') && <source src={bgVideo} type="video/webm" />}
					<source src={bgVideo.replace(/\.[^./]+$/, '.mp4')} type="video/mp4" />
				</video>
				{posterVisible && <webp.Picture src={preLoadImg} alt="" fallbackFormat={fallbackFormat} style={fallbackBackgroundStyle} />}
			</div>
		);
	}

	renderMain(props, state) {
		const { appState, backgroundStyle, fallbackBackgroundStyle, audioMuted, bgVideo } = state;

		const {
			activePage,
			appData,
			client,
			videos,
			header,
			showtimes,
			homeEnt,
			scrollerTallerThanWindow,
			widgetFullyExpanded
		} = appState;

		let mainPage = activePage;
		let activePageDetails = appData?.pages?.[activePage];
		if (activePageDetails?.section) {
			// TODO: ability to attach sections to different pages
			mainPage = Object.values(pages).find(page => page.page === 'home')?.id || 'boltHome';
		}
		// For overlays (e.g. boltShowtimes), activePage changes but it is not in pages
		//   when that's the case, keep using the previous one
		let page = pages[mainPage] || this.currentPage;
		if (activePageDetails?.customPageComponent && pages[activePageDetails.customPageComponent]) {
			page = { ...pages[activePageDetails.customPageComponent], id: activePageDetails.id };
		}
		if (props.renderedPage) {
			page = props.renderedPage;
		}
		this.currentPage = page;

		const orientation = state.orientation;
		const isMobile = (client && !client.isDesktopSize);
		const isMobileLandscape = (client?.deviceWidthSize === 'mobile') && (orientation === 'landscape');
		const isTabletLandscape = (client?.device !== 'desktop') && !isMobileLandscape && (orientation === 'landscape');
		const videoOverlayOpen = videos && videos.overlayOpen;
		const showtimesOverlayOpen = showtimes && showtimes.show;
		const showtimesAnimation = showtimes && showtimes.animate;
		const homeEntOverlayOpen = homeEnt && homeEnt.show;
		const homeEntAnimation = homeEnt && homeEnt.animate;
		const sidePanelOpen = showtimesOverlayOpen || homeEntOverlayOpen;
		const sidePanelAnimation = showtimesAnimation || homeEntAnimation;
		// const headerOpen = accessNested(this.state, 'appState.header.open', false);
		const homeActivePercentage = accessNested(appState, 'homeActivePercentage', 1);
		const hubActivePercentage = accessNested(appState, 'hubActivePercentage', 1);
		// Hides the background to prevent it being visible on iOS overscroll
		const hideBackground = !homeActivePercentage && isMobile;
		const hasStickyFooter = accessNested(appData, 'footer.sticky', false);
		const evidonCookieMode = accessNested(this.state, 'appState.evidon.bannerOpen', false);
		const cookieMode = evidonCookieMode || (accessNested(this.state, 'appState.cookies.pushDown', false) && accessNested(this.state, 'appState.cookies.cookieBannerOpen', false));
		const oneContinuousScroll = (evidonCookieMode && isMobile) || accessNested(appData, 'options.oneContinuousScroll'); // Makes the whole main page one big scrolling section to cater for the giant Evidon banner (or for other reasons).
		const sonyHeaderEnabled = accessNested(appData, 'options.sonyFeatures.sonyHeader', false) && !getIsSinglePageIframed();

		const sonyFooterEnabled = accessNested(appData, 'footer.sonyFooter', false);
		const accessibilityCopy = accessNested(appData, 'copy.accessibilityCopy', {});
		const disableMobileLandscape = accessNested(appData, 'options.disableLandscape');
		const disableTabletLandscape = accessNested(appData, 'options.disableTabletLandscape');
		const videoOverlay = accessNested(appData, 'options.videoOverlay', null);
		const landingAudio = accessNested(appData, 'options.landingAudio', null);
		const visibleScrollbar = accessNested(appData, 'options.paramountFeatures.visibleScrollbar', false);
		const iframeLandscapeEnabled = accessNested(appData, 'pages.boltIframe.data.disableOrientationLock', false) && activePage === 'boltIframe';
		const showLandscapeOverlay = ((isMobileLandscape && disableMobileLandscape) || (isTabletLandscape && disableTabletLandscape)) && !iframeLandscapeEnabled;
		const hasMiniGallery = accessNested(appData, 'options.paramountFeatures.toutCarousel.contentCarousel', false);
		const videoHasAudio = accessNested(appData, 'pages.boltHome.data.bg.videoHasAudio', false);
		const iconPosition = accessNested(appData, 'pages.boltHome.data.bg.iconPosition', 'top-left');

		const mainScrollStyle = {};
		if (visibleScrollbar) {
			mainScrollStyle.scrollbarColor = `${visibleScrollbar.color} ${visibleScrollbar.backgroundColor}`;
		}

		let headerOffset = 0;

		const bgCookieStyle = {};

		if (sonyHeaderEnabled) {
			headerOffset = 34;
		}

		if (cookieMode && typeof window !== 'undefined') {
			bgCookieStyle.position = oneContinuousScroll ? 'absolute' : 'fixed';
			const cookieHeight = (evidonCookieMode ? accessNested(appState, 'evidon.height') : accessNested(appState, 'cookies.height')) || 0;
			headerOffset += cookieHeight;
			bgCookieStyle.top = `${oneContinuousScroll ? '0' : headerOffset}px`;
			bgCookieStyle.height = (oneContinuousScroll ? window.innerHeight : window.innerHeight - headerOffset) + 'px';
		}

		// TODO: detect if ANY overlay is open in a generic way
		const headerHeight = accessNested(Store.get(), 'client.headerHeight', 0);
		const desktopSolidHeader = accessNested(appData, 'header.powsterHeader.desktopSolidHeader', null);
		const hub = accessNested(appData, 'pages.boltHubPage') && !accessNested(appData, 'pages.boltHome');
		const previewPanelReplacingHomePage = appData?.pages.boltPreviewPanel && !appData?.pages.boltHome;
		const dedicatedPage = pages[activePage] && activePage !== 'boltHome' && !activePageDetails?.section;
		const homeInactive = (homeActivePercentage <= getPercentageThreshold(headerOffset)) || activePage !== 'boltHome';
		const hubInactive = (hubActivePercentage <= getPercentageThreshold(headerHeight));
		let headerSolidBackground = hub ? hubInactive && !sidePanelOpen && !videoOverlayOpen : homeInactive && !sidePanelOpen && !videoOverlayOpen;
		if (previewPanelReplacingHomePage || !scrollerTallerThanWindow || widgetFullyExpanded) {
			headerSolidBackground = false;
		}
		// Potentially wait for other initial things to load
		const loaded = this.checkIfLoaded();

		let hasShowtimes = Object.values(appData.pages).some(p => p?.type === 'showtimes');
		let hasHomeEnt = Object.values(appData.pages).some(p => p?.type === 'homeEnt');
		let hasPressKit = Object.values(appData.pages).some(p => p?.type === 'pressKit');

		let bgRender = this.state.bgVideo ? this.renderBgVideo(homeActivePercentage, bgCookieStyle, fallbackBackgroundStyle) : (
			<div
				class={joinClasses({
					s, background: true,
					sidePanelOpen,
					hasStickyFooter,
					cookieMode,
					hideBackground,
					sidePanelAnimation,
					hasMiniGallery
				})}
				style={{ ...backgroundStyle, ...bgCookieStyle }}
				key="background"
				role="img"
				ariaLabel={accessibilityCopy.$BACKGROUND_IMAGE_ARIA}
			/>
		);

		let pageProps = {};
		if (page) {
			pageProps = {
				store: appState,
				client: appState.client,
				appData,
				id: page.id,
				data: appData.pages[page.id]?.data,
				bgRender: oneContinuousScroll && bgRender
			};
			if (page.page) {
				pageProps[page.page] = appState[page.page];
			}
		}

		return (
			<div
				class={joinClasses(
					s.mainScroll,
					loaded && 'contentLoaded',
					(!visibleScrollbar || isMobile) && s.hideScrollbar,
					dedicatedPage && s.dedicatedPage
				)}
				id="mainScroll"
				style={mainScrollStyle}
			>
				<Header
					closeAllOverlays={this.closeAllOverlays}
					client={client}
					appData={appData}
					activePage={activePage}
					solidBackground={headerSolidBackground}
					headerData={header}
					offsetTop={headerOffset}
					isCalendarRemindMode={this.isCalendarRemindMode}
					calRemindOpen={appState.calRemindOpen}
					desktopSolidHeader={desktopSolidHeader}
					footerData={appState.footer}
					key="header"
				/>
				<h1 class={s.pageName}>{this.getH1(appData, page, activePageDetails, activePage)}</h1>
				{bgRender}
				{this.CalRemind && <this.CalRemind appData={appData} open={appState.calRemindOpen} offsetTop={headerOffset} key="calendarRemind" />}
				<DynamicPageLoader
					class={s.pageContainer}
					containerClass={joinClasses(s.pageWrapper, 'pageWrapper')}
					page={page}
					props={pageProps}
					onLoad={this.onPageContentLoad}
					key="dynamicPageLoader"
				/>
				{(!sidePanelOpen || sonyFooterEnabled) && <Footer data={appData.footer} store={appState} key="footer" />}
				{VideoOverlayLoader && <VideoOverlayLoader store={appState} key="videoLoader" />}
				{hasShowtimes && <SidePanelLoader store={appState} key="showtimesLoader" />}
				{hasHomeEnt && <SidePanelLoader store={appState} key="homeEntLoader" />}
				{hasPressKit && <SidePanelLoader store={appState} key="pressKitLoader" />}
				{this.TrailerOverlay && <this.TrailerOverlay data={videoOverlay} store={appState} />}
				{this.PressKitGalleryOverlay && <this.PressKitGalleryOverlay store={appState} />}
				{this.LandingAudio && <this.LandingAudio data={landingAudio} offsetTop={headerOffset} store={appState} />}
				{(videoHasAudio && bgVideo) && (
					<div onClick={this.toggleAudio} class={joinClasses(s.sound, audioMuted && s.muted, s[iconPosition])}>
						<button class={s.icon} />
					</div>
				)}
				{(!videoOverlayOpen && showLandscapeOverlay) && <LandscapeOverlay key="landscape-overlay" />}
				<div class={s.loadingOccluder} key="occluder" />
				{this.getExtra()}
			</div>
		);
	}

	render(props, state) {
		const {
			appData,
			client,
			cookies,
			evidon,
			debugMode,
			footer,
			header
		} = state.appState;

		const cookiePrefsOpen = cookies.cookiePreferencesOpen;
		const cookieBannerOpen = cookies.cookieBannerOpen;
		let ageGateOpen = state.appState.ageGateOpen !== false;
		const ageGateOnlyOnPages = appData?.options?.ageGate?.onlyOnPages;
		if (ageGateOpen && ageGateOnlyOnPages) {
			const activePageIsSection = appData.pages[state.appState.activePage].section;
			ageGateOpen = ageGateOnlyOnPages.split(',').map(e => e.trim()).includes(state.appState.activePage) && !activePageIsSection;
		}

		const headerType = Object.keys(appData.header).find(name => name.endsWith('Header') && name !== 'powsterHeader');
		const footerType = ['sonyFooter'].find(e => appData.footer[e]) || 'defaultFooter';
		const navOpen = header.open;

		let DebugInfo = debugMode && this.DebugInfo;
		let { Cookies, CookiesPreference, AgeGate, ExternalLinkModal, SignUpForm } = this;
		const isRTL = appData?.styling?.fontVariables?.textDirection === 'rtl';

		const style = `--footer-covered-screen-size: ${footer?.coveredScreenSize || 0}px;`;

		return (
			<div
				class={joinClasses(
					s.container,
					client.device,
					client.orientation,
					client.isIOS && 'iOS',
					headerType,
					footerType,
					'page-' + state.appState.activePage,
					isRTL && 'isRTL'

				)}
				id="bolt"
				style={style}
			>
				{Cookies && <Cookies appData={appData} categories={pixelsCategories} hidden={!cookieBannerOpen} key="cookieBanner" />}
				{CookiesPreference && <CookiesPreference appData={appData} copy={appData.copy.baseCopy} categories={pixelsCategories} key="cookiesPref" cookiePrefsOpen={cookiePrefsOpen} />}
				{AgeGate && ageGateOpen && <AgeGate appData={appData} copy={appData.copy} key="ageGate" />}
				{ExternalLinkModal && <ExternalLinkModal appData={appData} externalLinkModal={state.appState.externalLinkModal} copy={appData.copy} key="externalLink" />}
				<div class={s.mainScrollWrapper} key="mainScrollWrapper">{this.renderMain(props, state)}</div>
				{SignUpForm && <SignUpForm data={appData?.options?.formEmbeds?.signUp} activePage={state.appState.activePage} clientData={client} cookies={cookies} evidon={evidon} showtimesProviderInterstitialOpen={state.appState.showtimes?.providerInterstitialOpen} navOpen={navOpen} key="signUp" />}
				{!!DebugInfo && <DebugInfo appData={appData} appState={state.appState} key="debug" />}
			</div>
		);
	}
}
